import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import { useLenis } from '@/components/generals/smooth-scroll'
import { useSectionTone } from '@/components/generals/tone'
import { TransitionLink } from '@/components/generals/transition-link'
import { LocalTime } from '@/components/generals/local-time'
import { navigation } from '@/constants/site'
import { profile, socials } from '@/content/profile'

export const Footer: FC = (): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const lenis = useLenis()

    useSectionTone(ref, '#0e0e0b', 'dark')

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start end', 'end end']
    })

    const wordmarkY = useTransform(scrollYProgress, [0.2, 1], ['55%', '0%'])

    const toTop = () => {
        if (lenis) {
            lenis.scrollTo(0, { duration: 1.6 })
            return
        }

        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    return (
        <footer
            ref={ref}
            id='footer'
            className='relative overflow-hidden bg-peat text-bone'
        >
            <div className='grid grid-cols-2 gap-x-6 gap-y-12 border-t border-bone/10 px-5 pb-10 pt-16 md:grid-cols-12 md:px-8 md:pt-24'>
                <div className='col-span-2 md:col-span-4'>
                    <p className='max-w-[22ch] font-heading text-[clamp(26px,2.6vw,38px)] leading-[1.08] tracking-[-0.015em]'>
                        Wildlife and wild places of Kosovo and Albania, photographed with patience.
                    </p>

                    <TransitionLink
                        href='/contact'
                        className='mt-8 inline-flex items-center gap-3 border-b border-bone/30 pb-1 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors hover:border-ember hover:text-ember'
                    >
                        Prints, licensing &amp; talks
                        <span aria-hidden='true'>&rarr;</span>
                    </TransitionLink>
                </div>

                <nav aria-label='Footer' className='md:col-span-2 md:col-start-6'>
                    <h2 className='mb-5 font-mono text-[10px] uppercase tracking-[0.2em] text-fog'>
                        Pages
                    </h2>

                    <ul className='flex flex-col gap-2.5 text-[15px]'>
                        {navigation.map(item => (
                            <li key={item.href}>
                                <TransitionLink href={item.href} className='transition-colors hover:text-ember'>
                                    {item.label}
                                </TransitionLink>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='md:col-span-2'>
                    <h2 className='mb-5 font-mono text-[10px] uppercase tracking-[0.2em] text-fog'>
                        Elsewhere
                    </h2>

                    <ul className='flex flex-col gap-2.5 text-[15px]'>
                        {socials.map(social => (
                            <li key={social.href}>
                                <a
                                    href={social.href}
                                    target='_blank'
                                    rel='noreferrer'
                                    className='group inline-flex items-center gap-2 transition-colors hover:text-ember'
                                >
                                    {social.label}
                                    <span aria-hidden='true' className='text-[11px] opacity-0 transition-opacity group-hover:opacity-100'>
                                        &#8599;
                                    </span>
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='col-span-2 md:col-span-2'>
                    <h2 className='mb-5 font-mono text-[10px] uppercase tracking-[0.2em] text-fog'>
                        Base
                    </h2>

                    <p className='text-[15px] leading-relaxed'>
                        {profile.baseLocal}, Kosovo
                        <br />
                        <span className='text-fog'>{profile.coordinates}</span>
                        <br />
                        <span className='text-fog'>Local time </span>
                        <LocalTime />
                    </p>
                </div>
            </div>

            <div aria-hidden='true' className='relative overflow-hidden px-3 md:px-6'>
                <motion.p
                    style={{ y: wordmarkY }}
                    className='select-none whitespace-nowrap font-heading text-[17.2vw] leading-[0.8] tracking-[-0.055em] text-bone'
                >
                    Arian Mavriqi
                </motion.p>
            </div>

            <div className='flex flex-col gap-4 border-t border-bone/10 px-5 py-6 font-mono text-[10px] uppercase tracking-[0.18em] text-fog md:flex-row md:items-center md:justify-between md:px-8'>
                <p suppressHydrationWarning>
                    &copy; {new Date().getFullYear()} Arian Mavriqi. All photographs &copy; Arian Mavriqi.
                </p>

                <button
                    type='button'
                    onClick={toTop}
                    className='self-start text-bone transition-colors hover:text-ember md:self-auto'
                >
                    Back to the top &uarr;
                </button>
            </div>
        </footer>
    )
}
